import React, { FC } from 'react';
import AsdPathwayCard from './asd-pathway-card';
import type { FeeQuote } from '../utils/fee';
import {
  FEE_ASSESSMENT_TYPE,
  isAutismAssessment,
  PRESCRIPTION_TYPE,
  REGION_TYPE,
  SEEN_PSYCHIATRIST,
} from '../utils/fee';
import type { FeeValues } from '../types/flow';

interface FeeSummaryDetailsProps {
  feeEstimate: FeeQuote;
  feePrescriptionType: FeeValues['prescriptionType'];
  feeLocation: FeeValues['location'];
  feeAssessmentType: FeeValues['feeAssessmentScope'];
  feeSeenPsychiatrist: FeeValues['seenPsychiatrist'];
  /** Medicare or DVA card holder; rebate wording is hidden without a card. */
  hasMedicareCard: boolean;
  compact?: boolean;
}

const PACKAGE_LABELS: Partial<Record<typeof PRESCRIPTION_TYPE[keyof typeof PRESCRIPTION_TYPE], string>> = {
  [PRESCRIPTION_TYPE.gp]: 'Standard (GP prescribing)',
  [PRESCRIPTION_TYPE.telecare]: 'Script Start Package',
};

const ASSESSMENT_TYPE_LABELS: Partial<Record<typeof FEE_ASSESSMENT_TYPE[keyof typeof FEE_ASSESSMENT_TYPE], string>> = {
  [FEE_ASSESSMENT_TYPE.adhd]: 'ADHD',
  [FEE_ASSESSMENT_TYPE.autism]: 'Autism (ASD)',
  [FEE_ASSESSMENT_TYPE.combined]: 'ADHD + Autism',
};

const FeeSummaryDetails: FC<FeeSummaryDetailsProps> = ({
  feeEstimate,
  feePrescriptionType,
  feeLocation,
  feeAssessmentType,
  feeSeenPsychiatrist,
  hasMedicareCard,
  compact = false,
}) => {
  const showDualMetroNotice = feeEstimate.hasAsdPathway;

  const rowClass = 'flex justify-between gap-4 text-sm py-2 text-slate-500';
  const valueClass = 'font-semibold text-slate-800 text-right';

  return (
    <div className={compact ? '' : 'bg-slate-50 border border-slate-200 rounded-xl p-4'}>
      {!compact && <p className="text-sm font-bold text-slate-700 mb-3">Fee Summary</p>}
      <div className="space-y-0 mb-3">
        <div className={rowClass}>
          <span>Assessment type</span>
          <span className={valueClass}>
            {(feeAssessmentType && ASSESSMENT_TYPE_LABELS[feeAssessmentType])
              || 'Not specified'}
          </span>
        </div>
        <div className={rowClass}>
          <span>Location</span>
          <span className={valueClass}>
            {feeLocation === REGION_TYPE.metro ? 'Metro' : 'Rural / Regional'}
          </span>
        </div>
        <div className={rowClass}>
          <span>Seen psychiatrist in past 12 months</span>
          <span className={valueClass}>
            {feeSeenPsychiatrist === SEEN_PSYCHIATRIST.yes ? 'Yes' : 'No'}
          </span>
        </div>
        {!isAutismAssessment(feeAssessmentType) && (
          <div className={rowClass}>
            <span>Prescribing</span>
            <span className={valueClass}>
              {(feePrescriptionType && PACKAGE_LABELS[feePrescriptionType])
                || feePrescriptionType
                || 'Not specified'}
            </span>
          </div>
        )}
      </div>
      <div className="space-y-1 pt-3 border-t border-slate-200">
        <div className={rowClass}>
          <span>Consultation fee (estimate)</span>
          <span className={valueClass}>
            $
            {feeEstimate.gross.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-slate-300">
          <span className="font-bold text-slate-800">Estimated fee</span>
          <span className="font-bold text-2xl text-brand">
            $
            {feeEstimate.gross.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-3 mt-4 text-xs leading-relaxed text-amber-900">
        <strong>{hasMedicareCard ? 'Private fee — no Medicare rebate applies.' : 'Private fee.'}</strong>
        {' '}
        Our team verifies your eligibility afterwards and will contact you if the fee changes.
      </div>

      <div className="rounded-lg border-l-4 border-brand bg-brand bg-opacity-5 p-3 mt-3 text-xs leading-relaxed text-brand-mid">
        Invoice by SMS. Payment due
        {' '}
        <strong>72 business hours</strong>
        {' '}
        before your appointment.
      </div>

      {!compact && (
        <p className="text-xs text-muted mt-3 leading-relaxed">
          A digital invoice will be sent via
          {' '}
          <strong>SMS</strong>
          . Payment required
          {' '}
          <strong>72 business hours prior</strong>
          {' '}
          to your in-take appointment.
        </p>
      )}

      {/* Shown in compact mode too: the mobile fee sheet is the only place phones see it. */}
      {showDualMetroNotice && (
        <AsdPathwayCard consultationFee={feeEstimate.gross} hasMedicareCard={hasMedicareCard} />
      )}

      {!compact && (
        <>
          <p className="text-xs text-muted mt-3 leading-relaxed">
            This fee has been calculated based on the information you provided. Our team
            will verify your eligibility and details afterwards and contact you if any fee
            adjustment is required.
          </p>
          <p className="text-xs text-slate-700 mt-2 leading-relaxed font-semibold">
            {hasMedicareCard
              ? 'Please note: This is a private fee, and NO Medicare rebate applies to this fee.'
              : 'Please note: This is a private fee.'}
            {showDualMetroNotice && (
              <>
                {' '}
                If you are eligible, your first psychiatrist appointment following the intake
                appointment
                {hasMedicareCard ? ' will be bulk billed.' : ' will have no upfront fee.'}
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
};

export default FeeSummaryDetails;
